import { useEffect, useRef, useCallback } from 'react';

/**
 * useMetaPixel — loads the Meta (Facebook) Pixel base script once and fires
 * PageView + custom conversion events.
 *
 * Isolation: Only injected on public tenant pages (/{slug}/*) — the pixel ID
 * comes from that tenant's `/api/public/*` metadata endpoint. Admin pages do
 * NOT load any pixel. Different tenants never share the same fbq() instance
 * because each public URL loads exactly one tenant's pixel.
 *
 * Usage:
 *   const { trackEvent } = useMetaPixel(pixelId, testEventCode);
 *   trackEvent('Lead', { content_name: 'Supporter Signup' });
 *
 * Returns:
 *   - trackEvent(name, params) — fires fbq('track', name, params)
 *   - ready — true when the script has finished loading
 */
export function useMetaPixel(pixelId, testEventCode) {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (!pixelId) return undefined;
    if (typeof window === 'undefined') return undefined;
    // Guard against re-initialization on hot-reload / route changes for the
    // same pixel. If a different pixel is passed, we simply skip — one pixel
    // per document lifetime keeps tenant isolation strict.
    if (initializedRef.current) return undefined;
    initializedRef.current = true;

    // Vanilla Meta Pixel loader (from Meta's official snippet, formatted).
    // Reads/writes only `window.fbq` and the injected `<script>` tag.
    (function loadPixel(f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = true;
      t.src = v;
      t.setAttribute('data-meadd-pixel', '1');
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', pixelId);
    const opts = testEventCode ? { test_event_code: testEventCode } : undefined;
    window.fbq('track', 'PageView', undefined, opts);

    // Add a noscript <img> pixel as a fallback (Meta recommends it).
    const noscript = document.createElement('noscript');
    const img = document.createElement('img');
    img.height = 1; img.width = 1;
    img.style.display = 'none';
    img.src = `https://www.facebook.com/tr?id=${encodeURIComponent(pixelId)}&ev=PageView&noscript=1`;
    noscript.appendChild(img);
    document.body.appendChild(noscript);

    return () => {
      // We intentionally do NOT rip fbq from the window here — the browser
      // will unload the tab shortly and removing the script would confuse
      // in-flight beacons.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixelId]);

  const trackEvent = useCallback(
    (eventName, params) => {
      if (!pixelId) return;
      if (typeof window === 'undefined' || !window.fbq) return;
      try {
        const opts = testEventCode ? { test_event_code: testEventCode } : undefined;
        window.fbq('track', eventName, params || undefined, opts);
      } catch (_) { /* swallow: pixel failures must never break UX */ }
    },
    [pixelId, testEventCode]
  );

  return { trackEvent, ready: !!pixelId && initializedRef.current };
}
